import {
    BugReportRounded,
    EmailRounded,
    ExpandMoreRounded,
} from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Chip,
    Collapse,
    IconButton,
    Paper,
    Tooltip,
    Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import AcxConfirmationDialog from "components/UI/AcxConfirmationDialog";
import AcxEmailChipInput, {
    AcxEmailChipInputHandle,
    EmailSuggestion,
} from "components/UI/AcxEmailChipInput";
import {
    KboFlowRunOutput,
    KboNodeSelectedFinding,
    KboReport,
    KboReportFindingContent,
} from "../KboFlowDebug/KboFlowRunDebug";
import { formatNumber } from "../KboFlowDebug/KboFlowDebugUI";
import {
    formatPercent,
    getExploration,
    getV7AnalysisOnlyResults,
    getHowKboGotHereEntries,
    getHowKboGotHereEntriesForPath,
    getInvocationSkills,
    getV7ExploredLeafResults,
    getV7FindingPath,
    getV7ReportFindings,
    getV7RootNode,
    HowKboGotHereEntry,
    V7AnalysisOnlyResult,
    V7ExploredLeafResult,
} from "./KboFlowRunDigestModels";
import { StepReviewPanel } from "./KboFlowRunFeedback";
import { kboFlowRunDetailColors as pageColors } from "./KboFlowRunDetailStyles";
import { AnalysisMaterialAccordion } from "./KboFlowRunAnalysisMaterial";

type DeliveredFinding = {
    finding: KboReportFindingContent;
    report: KboReport;
    reportIndex: number;
    id: string;
};

export type FindingsStepDebugTarget = {
    stepId: "analyze-one-node" | "report-assembly";
    nodeId?: string;
    /** The result that led the user to this node; Analyze Node still reruns the full node. */
    resultTitle?: string;
};

type ResultOutcome =
    | "Included in report"
    | "Pursued deeper"
    | "Not pursued deeper";

function ResultOutcomeBadge({ outcome }: { outcome: ResultOutcome }) {
    const sx = {
        "Included in report": {
            bgcolor: pageColors.greenSoft,
            color: pageColors.green,
        },
        "Pursued deeper": {
            bgcolor: pageColors.blueSoft,
            color: pageColors.blue,
        },
        "Not pursued deeper": {
            bgcolor: "grey.100",
            color: pageColors.muted,
        },
    }[outcome];
    return (
        <Chip
            label={outcome}
            size="small"
            sx={{
                ...sx,
                flex: "0 0 auto",
                fontSize: 11,
                fontWeight: 700,
                height: 22,
            }}
        />
    );
}

function resultOf(selected?: KboNodeSelectedFinding) {
    return selected?.result ?? selected?.finding;
}

function metricLabel(selected?: KboNodeSelectedFinding) {
    return resultMetricLabel(resultOf(selected));
}

function resultMetricLabel(result?: {
    conversationCount?: number;
    totalConversationsReviewed?: number;
    conversationShare?: number;
}) {
    const numerator = result?.conversationCount;
    const denominator =
        typeof result?.totalConversationsReviewed === "number" &&
        (typeof numerator !== "number" ||
            result.totalConversationsReviewed >= numerator)
            ? result.totalConversationsReviewed
            : undefined;
    const share = result?.conversationShare;
    if (typeof numerator !== "number" && typeof denominator !== "number")
        return undefined;
    const counts =
        typeof numerator === "number"
            ? `${formatNumber(numerator)}${
                  typeof denominator === "number"
                      ? ` of ${formatNumber(denominator)}`
                      : ""
              }`
            : `${formatNumber(denominator!)} reviewed`;
    const percentage = formatPercent(share);
    return percentage === "—" ? counts : `${counts} · ${percentage}`;
}

function statusTone(status?: string) {
    const normalized = status?.toLowerCase() ?? "";
    if (normalized.includes("fail")) return "error" as const;
    if (normalized.includes("pending") || normalized.includes("running"))
        return "warning" as const;
    return "success" as const;
}

function EmailPanel({
    report,
    reportIndex,
    suggestions,
    suggestionsLoading,
    submitting,
    onSend,
}: {
    report: KboReport;
    reportIndex: number;
    suggestions: EmailSuggestion[];
    suggestionsLoading: boolean;
    submitting: boolean;
    onSend: (recipients: string[], reportIndex: number) => Promise<boolean>;
}) {
    const [open, setOpen] = useState(false);
    const [sendOpen, setSendOpen] = useState(false);
    const [recipients, setRecipients] = useState<string[]>([]);
    const [pendingText, setPendingText] = useState("");
    const inputRef = useRef<AcxEmailChipInputHandle>(null);
    const email = report.email;
    if (!email) return null;
    return (
        <Paper
            variant="outlined"
            sx={{
                borderColor: pageColors.green,
                borderRadius: 2,
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    alignItems: "center",
                    display: "flex",
                    gap: 1.25,
                    p: { xs: 1.5, md: 2 },
                }}
            >
                <EmailRounded sx={{ color: pageColors.green }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 800 }}>
                        {email.subject ?? "KBo report"}
                    </Typography>
                    <Typography sx={{ color: pageColors.muted, fontSize: 13 }}>
                        {email.previewText ?? "What the recipient received"}
                    </Typography>
                </Box>
                <Button size="small" onClick={() => setOpen((value) => !value)}>
                    {open ? "Hide preview" : "View email preview"}
                </Button>
                <Button
                    size="small"
                    variant="contained"
                    onClick={() => setSendOpen(true)}
                >
                    Send email
                </Button>
            </Box>
            <Collapse in={open} timeout={180} mountOnEnter>
                {email.htmlBody ? (
                    <Box
                        component="iframe"
                        title={email.subject ?? "Generated report"}
                        srcDoc={email.htmlBody}
                        sandbox=""
                        sx={{
                            border: 0,
                            display: "block",
                            height: 560,
                            width: "100%",
                        }}
                    />
                ) : (
                    <Box
                        sx={{
                            fontFamily: "monospace",
                            p: 2,
                            whiteSpace: "pre-wrap",
                        }}
                    >
                        {email.plainTextBody ?? "No email body was recorded."}
                    </Box>
                )}
            </Collapse>
            <AcxConfirmationDialog
                isOpen={sendOpen}
                title="Send report"
                subTitle="Enter one or more email addresses. Leave empty to send the report to yourself."
                maxWidth="sm"
                fullWidth
                onClose={() => setSendOpen(false)}
                onConfirm={async () => {
                    const finalRecipients =
                        inputRef.current?.flushPending() ?? recipients;
                    if (await onSend(finalRecipients, reportIndex))
                        setSendOpen(false);
                }}
                isLoading={submitting}
                confirmButtonText={
                    recipients.length || pendingText.trim()
                        ? "Send report"
                        : "Send to me"
                }
                content={
                    <AcxEmailChipInput
                        ref={inputRef}
                        values={recipients}
                        setValues={setRecipients}
                        suggestions={suggestions}
                        loading={suggestionsLoading}
                        placeholderText="Add email addresses…"
                        onPendingTextChange={setPendingText}
                    />
                }
            />
        </Paper>
    );
}

function DetailText({ label, value }: { label: string; value?: string }) {
    if (!value) return null;
    return (
        <Box sx={{ mt: 1.25 }}>
            <Typography
                sx={{
                    color: pageColors.muted,
                    fontSize: 11,
                    fontWeight: 800,
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                }}
            >
                {label}
            </Typography>
            <Typography
                sx={{ color: pageColors.ink, lineHeight: 1.55, mt: 0.25 }}
            >
                {value}
            </Typography>
        </Box>
    );
}

function timelineMetric(
    entry: Pick<HowKboGotHereEntry, "count" | "denominator">,
): string | undefined {
    if (typeof entry.count !== "number") return undefined;
    const count = formatNumber(entry.count);
    if (typeof entry.denominator === "number") {
        return `${count} of ${formatNumber(entry.denominator)}`;
    }
    return `${count} conversations`;
}

function TimelineRationale({
    text,
    hideToggleUnlessClamped = false,
}: {
    text: string;
    hideToggleUnlessClamped?: boolean;
}) {
    const textRef = useRef<HTMLParagraphElement>(null);
    const [expanded, setExpanded] = useState(false);
    const [isClamped, setIsClamped] = useState(false);

    useEffect(() => {
        const checkIsClamped = () => {
            const element = textRef.current;
            setIsClamped(
                !!element && element.scrollHeight > element.clientHeight,
            );
        };
        checkIsClamped();
        window.addEventListener("resize", checkIsClamped);
        return () => window.removeEventListener("resize", checkIsClamped);
    }, [text]);

    const showToggle = !hideToggleUnlessClamped || isClamped;
    return (
        <Box sx={{ mt: 0.25 }}>
            <Typography
                ref={textRef}
                sx={{
                    color: pageColors.muted,
                    display: expanded ? "block" : "-webkit-box",
                    fontSize: 13,
                    lineHeight: 1.45,
                    overflow: "hidden",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: expanded ? "unset" : 1,
                }}
            >
                {text}
            </Typography>
            {showToggle && (
                <Button
                    aria-expanded={expanded}
                    onClick={() => setExpanded((value) => !value)}
                    size="small"
                    sx={{ fontSize: 12, minWidth: 0, mt: 0.25, px: 0 }}
                >
                    {expanded ? "Show less" : "Show more"}
                </Button>
            )}
        </Box>
    );
}

function HowKboGotHereTimeline({
    entries,
    onNavigateToResult,
    resultStatus,
    runId,
    onOpenStepDebug,
}: {
    entries: HowKboGotHereEntry[];
    onNavigateToResult?: (resultId: string) => void;
    resultStatus?: (resultId: string) => ResultOutcome | undefined;
    runId?: string;
    onOpenStepDebug?: (target: FindingsStepDebugTarget) => void;
}) {
    return (
        <Box sx={{ display: "flex", flexDirection: "column", mt: 1.25 }}>
            {entries.map((entry, index) => (
                <HowKboGotHereTimelineEntry
                    entry={entry}
                    hasNext={index < entries.length - 1}
                    key={`${entry.kind}-${index}`}
                    number={index + 1}
                    onNavigateToResult={onNavigateToResult}
                    onOpenStepDebug={onOpenStepDebug}
                    resultStatus={resultStatus}
                    runId={runId}
                />
            ))}
        </Box>
    );
}

function HowKboGotHereTimelineEntry({
    entry,
    number,
    hasNext,
    onNavigateToResult,
    onOpenStepDebug,
    resultStatus,
    runId,
}: {
    entry: HowKboGotHereEntry;
    number: number;
    hasNext: boolean;
    onNavigateToResult?: (resultId: string) => void;
    onOpenStepDebug?: (target: FindingsStepDebugTarget) => void;
    resultStatus?: (resultId: string) => ResultOutcome | undefined;
    runId?: string;
}) {
    const [peersOpen, setPeersOpen] = useState(false);
    const metric = timelineMetric(entry);
    const percentage = formatPercent(entry.share);
    const peers = entry.peerResults ?? [];
    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: "32px minmax(0, 1fr)",
                minHeight: 76,
            }}
        >
            <Box
                sx={{
                    alignItems: "center",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <Box
                    sx={{
                        alignItems: "center",
                        bgcolor:
                            entry.kind === "finding"
                                ? pageColors.green
                                : pageColors.white,
                        border: "1px solid",
                        borderColor:
                            entry.kind === "finding"
                                ? pageColors.green
                                : "#D0D5DD",
                        borderRadius: "50%",
                        color:
                            entry.kind === "finding"
                                ? pageColors.white
                                : pageColors.blue,
                        display: "flex",
                        flex: "0 0 auto",
                        fontSize: 12,
                        fontWeight: 800,
                        height: 24,
                        justifyContent: "center",
                        width: 24,
                    }}
                >
                    {number}
                </Box>
                {hasNext && (
                    <Box
                        sx={{
                            bgcolor: "#D0D5DD",
                            flex: 1,
                            my: 0.5,
                            width: "1px",
                        }}
                    />
                )}
            </Box>
            <Box sx={{ minWidth: 0, pb: 1.75, pl: 1 }}>
                <Box sx={{ alignItems: "center", display: "flex" }}>
                    <Typography
                        sx={{
                            color: pageColors.muted,
                            fontSize: 11,
                            fontWeight: 800,
                            letterSpacing: "0.06em",
                            textTransform: "uppercase",
                        }}
                    >
                        {entry.label}
                    </Typography>
                    {entry.kind === "finding" &&
                        entry.nodeId &&
                        onOpenStepDebug && (
                            <Tooltip title="Debug this analysis step">
                                <IconButton
                                    aria-label={`Debug analysis: ${entry.title}`}
                                    color="primary"
                                    size="small"
                                    sx={{ ml: 0.25, p: 0.25 }}
                                    onClick={() =>
                                        onOpenStepDebug({
                                            stepId: "analyze-one-node",
                                            nodeId: entry.nodeId,
                                            resultTitle: entry.title,
                                        })
                                    }
                                >
                                    <BugReportRounded sx={{ fontSize: 15 }} />
                                </IconButton>
                            </Tooltip>
                        )}
                </Box>
                <Typography
                    sx={{
                        color:
                            entry.kind === "finding"
                                ? pageColors.green
                                : pageColors.ink,
                        fontSize: 15,
                        fontWeight: 800,
                        lineHeight: 1.35,
                        mt: 0.2,
                    }}
                >
                    {entry.title}
                    {metric ? ` · ${metric}` : ""}
                    {percentage !== "—" ? ` · ${percentage}` : ""}
                </Typography>
                {entry.detail &&
                    (entry.kind === "cohort" ? (
                        <TimelineRationale
                            text={entry.detail}
                            hideToggleUnlessClamped
                        />
                    ) : (
                        <Typography
                            sx={{
                                color: pageColors.muted,
                                fontSize: 13,
                                lineHeight: 1.45,
                                mt: 0.25,
                            }}
                        >
                            {entry.detail}
                        </Typography>
                    ))}
                {entry.rationale && (
                    <TimelineRationale text={entry.rationale} />
                )}
                {entry.kind === "finding" && entry.resultId && (
                    <AnalysisMaterialAccordion
                        hasMatchingConversationIds={
                            !!entry.hasMatchingConversationIds
                        }
                        nodeId={entry.nodeId}
                        resultId={entry.resultId}
                        resultName={entry.title}
                        runId={runId}
                    />
                )}
                {peers.length > 0 && (
                    <Box sx={{ mt: 0.75 }}>
                        <Button
                            aria-expanded={peersOpen}
                            onClick={() => setPeersOpen((open) => !open)}
                            size="small"
                            sx={{ fontSize: 12, minWidth: 0, px: 0 }}
                        >
                            Other analysis results ({peers.length})
                        </Button>
                        <Collapse in={peersOpen} timeout={180} mountOnEnter>
                            <Box
                                sx={{
                                    borderLeft: "2px solid",
                                    borderColor: pageColors.greenSoft,
                                    display: "flex",
                                    flexDirection: "column",
                                    mt: 0.5,
                                    pl: 1,
                                }}
                            >
                                {peers.map((peer) => {
                                    const peerMetric = timelineMetric(peer);
                                    const peerShare = formatPercent(peer.share);
                                    const outcome =
                                        resultStatus?.(peer.resultId) ??
                                        "Not pursued deeper";
                                    const label = [
                                        peer.result.name ??
                                            "Analysis result not retained",
                                        peerMetric,
                                        peerShare !== "—"
                                            ? peerShare
                                            : undefined,
                                    ]
                                        .filter(Boolean)
                                        .join(" · ");
                                    return (
                                        <Box key={peer.resultId}>
                                            <AnalysisMaterialAccordion
                                                badge={
                                                    <ResultOutcomeBadge
                                                        outcome={outcome}
                                                    />
                                                }
                                                hasMatchingConversationIds={
                                                    peer.hasMatchingConversationIds
                                                }
                                                nodeId={entry.nodeId}
                                                onViewFullPath={
                                                    onNavigateToResult &&
                                                    (() =>
                                                        onNavigateToResult(
                                                            peer.resultId,
                                                        ))
                                                }
                                                resultId={peer.resultId}
                                                resultName={label}
                                                runId={runId}
                                                titleColor={pageColors.ink}
                                            />
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Collapse>
                    </Box>
                )}
            </Box>
        </Box>
    );
}

function FindingCard({
    item,
    output,
    number,
    expanded,
    onExpandedChange,
    onNavigateToResult,
    resultStatus,
    runId,
    onOpenStepDebug,
}: {
    item: DeliveredFinding;
    output: KboFlowRunOutput;
    number: number;
    expanded: boolean;
    onExpandedChange: (expanded: boolean) => void;
    onNavigateToResult: (resultId: string) => void;
    resultStatus: (resultId: string) => ResultOutcome | undefined;
    runId?: string;
    onOpenStepDebug?: (target: FindingsStepDebugTarget) => void;
}) {
    const path = getV7FindingPath(output, item.report, item.finding);
    const howKboGotHere = getHowKboGotHereEntries(
        output,
        item.report,
        item.finding,
    );
    const selected = path.steps.at(-1)?.selected;
    const metric = metricLabel(selected);
    const questionId =
        item.report.questionId ?? path.steps[0]?.node?.rootQuestionId;
    const pathNodeIds = path.steps
        .map((step) => step.node?.nodeId)
        .filter((id): id is string => !!id);
    return (
        <Paper
            id={`finding-${number}`}
            variant="outlined"
            sx={{
                borderColor: pageColors.green,
                borderRadius: 2,
                overflow: "hidden",
                scrollMarginTop: 16,
            }}
        >
            <Box
                component="button"
                type="button"
                onClick={() => onExpandedChange(!expanded)}
                sx={{
                    bgcolor: "transparent",
                    border: 0,
                    color: pageColors.ink,
                    cursor: "pointer",
                    display: "block",
                    font: "inherit",
                    p: { xs: 1.75, md: 2.25 },
                    textAlign: "left",
                    width: "100%",
                }}
            >
                <Box sx={{ alignItems: "flex-start", display: "flex", gap: 1 }}>
                    <Box
                        sx={{
                            alignItems: "center",
                            bgcolor: pageColors.greenSoft,
                            borderRadius: "50%",
                            color: pageColors.green,
                            display: "flex",
                            flex: "0 0 auto",
                            fontWeight: 800,
                            height: 30,
                            justifyContent: "center",
                            width: 30,
                        }}
                    >
                        {number}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: 20, fontWeight: 800 }}>
                            {item.finding.title ?? "Untitled finding"}
                        </Typography>
                        {/* <Typography
                            sx={{
                                color: pageColors.muted,
                                lineHeight: 1.55,
                                mt: 0.5,
                            }}
                        >
                            {item.finding.digest}
                        </Typography> */}
                        {/* <Typography
                            sx={{
                                color: pageColors.muted,
                                fontSize: 13,
                                mt: 1,
                            }}
                        >
                            {path.steps.length
                                ? `${path.steps.length} reasoning ${
                                      path.steps.length === 1 ? "step" : "steps"
                                  } · open to inspect`
                                : "Reasoning path not recorded"}
                        </Typography> */}
                    </Box>
                    {metric && (
                        <Chip
                            size="small"
                            label={metric}
                            sx={{
                                bgcolor: pageColors.blueSoft,
                                color: pageColors.blue,
                                fontWeight: 750,
                            }}
                        />
                    )}
                    <ExpandMoreRounded
                        sx={{
                            transform: expanded ? "rotate(180deg)" : "none",
                            transition: "transform 120ms",
                        }}
                    />
                </Box>
            </Box>
            <Collapse in={expanded} timeout={180} mountOnEnter>
                <Box
                    sx={{
                        borderTop: "1px solid",
                        borderColor: pageColors.greenSoft,
                        p: { xs: 1.75, md: 2.25 },
                    }}
                >
                    <DetailText label="" value={item.finding.digest} />
                    <DetailText
                        label="Why it matters"
                        value={item.finding.whyItMatters}
                    />
                    <DetailText
                        label="Take action"
                        value={item.finding.action}
                    />
                    {/* <DetailText
                        label="How KBo got here"
                        value={
                            item.finding.howIGotHere ??
                            item.finding.provenanceText
                        }
                    /> */}
                    <Typography
                        component="h3"
                        sx={{
                            color: pageColors.green,
                            fontSize: 12,
                            fontWeight: 800,
                            letterSpacing: "0.06em",
                            mt: 2,
                            textTransform: "uppercase",
                        }}
                    >
                        How KBo Got Here
                    </Typography>
                    {howKboGotHere.length ? (
                        <HowKboGotHereTimeline
                            entries={howKboGotHere}
                            onNavigateToResult={onNavigateToResult}
                            onOpenStepDebug={onOpenStepDebug}
                            resultStatus={resultStatus}
                            runId={runId}
                        />
                    ) : (
                        <Alert severity="info" sx={{ mt: 1 }}>
                            This email finding has no retained exploration path.
                        </Alert>
                    )}
                    {questionId && (
                        <StepReviewPanel
                            questionId={questionId}
                            scope={{ kind: "question" }}
                            trace={{
                                runId: output.runId,
                                nodeId: pathNodeIds.at(-1),
                                findingId: item.finding.findingId,
                                resultId: item.finding.leafAnalysisResultId,
                                pathNodeIds,
                                step: "Final emailed finding",
                                skills: [
                                    ...new Set(
                                        path.steps.flatMap((step) =>
                                            getInvocationSkills(
                                                step.node?.invocations,
                                            ),
                                        ),
                                    ),
                                ],
                            }}
                            label="Review this result"
                            placeholder="Does this result belong in the report? What evidence or reasoning is missing?"
                        />
                    )}
                </Box>
            </Collapse>
        </Paper>
    );
}

function OtherPaths({
    output,
    report,
    expanded,
    onExpandedChange,
    expandedResultIds,
    onResultExpandedChange,
    onNavigateToResult,
    resultStatus,
    runId,
}: {
    output: KboFlowRunOutput;
    report: KboReport;
    expanded: boolean;
    onExpandedChange: (expanded: boolean) => void;
    expandedResultIds: Set<string>;
    onResultExpandedChange: (resultId: string, expanded: boolean) => void;
    onNavigateToResult: (resultId: string) => void;
    resultStatus: (resultId: string) => ResultOutcome | undefined;
    runId?: string;
}) {
    const results = getV7ExploredLeafResults(output, report);
    if (!results.length) return null;
    return (
        <Paper
            id="other-paths"
            variant="outlined"
            sx={{ borderRadius: 2, scrollMarginTop: 16 }}
        >
            <Box
                component="button"
                type="button"
                onClick={() => onExpandedChange(!expanded)}
                sx={{
                    alignItems: "center",
                    bgcolor: "transparent",
                    border: 0,
                    color: pageColors.ink,
                    cursor: "pointer",
                    display: "flex",
                    font: "inherit",
                    gap: 1,
                    p: 2,
                    textAlign: "left",
                    width: "100%",
                }}
            >
                <ExpandMoreRounded
                    sx={{
                        transform: expanded ? "rotate(180deg)" : "none",
                        transition: "transform 120ms",
                    }}
                />
                <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 800 }}>
                        Everything else KBo explored
                    </Typography>
                    <Typography sx={{ color: pageColors.muted, fontSize: 13 }}>
                        {results.length} result{results.length === 1 ? "" : "s"}{" "}
                        not included in this report
                    </Typography>
                </Box>
            </Box>
            <Collapse in={expanded} timeout={180} mountOnEnter>
                <Box
                    sx={{
                        borderTop: "1px dashed",
                        borderColor: "divider",
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        p: 1.5,
                    }}
                >
                    {results.map((result, index) => (
                        <ExploredResultCard
                            key={result.result.resultId ?? index}
                            result={result}
                            index={index + 1}
                            expanded={
                                !!result.result.resultId &&
                                expandedResultIds.has(result.result.resultId)
                            }
                            onExpandedChange={(nextExpanded) => {
                                if (result.result.resultId) {
                                    onResultExpandedChange(
                                        result.result.resultId,
                                        nextExpanded,
                                    );
                                }
                            }}
                            onNavigateToResult={onNavigateToResult}
                            resultStatus={resultStatus}
                            runId={runId}
                        />
                    ))}
                </Box>
            </Collapse>
        </Paper>
    );
}

function AnalysisOnlyResults({
    results,
    expanded,
    onExpandedChange,
    expandedResultIds,
    onResultExpandedChange,
    onNavigateToResult,
    resultStatus,
    runId,
}: {
    results: V7AnalysisOnlyResult[];
    expanded: boolean;
    onExpandedChange: (expanded: boolean) => void;
    expandedResultIds: Set<string>;
    onResultExpandedChange: (resultId: string, expanded: boolean) => void;
    onNavigateToResult: (resultId: string) => void;
    resultStatus: (resultId: string) => ResultOutcome | undefined;
    runId?: string;
}) {
    if (!results.length) return null;
    return (
        <Paper
            id="analysis-only-results"
            variant="outlined"
            sx={{ borderRadius: 2, scrollMarginTop: 16 }}
        >
            <Box
                component="button"
                type="button"
                onClick={() => onExpandedChange(!expanded)}
                sx={{
                    alignItems: "center",
                    bgcolor: "transparent",
                    border: 0,
                    color: pageColors.ink,
                    cursor: "pointer",
                    display: "flex",
                    font: "inherit",
                    gap: 1,
                    p: 2,
                    textAlign: "left",
                    width: "100%",
                }}
            >
                <ExpandMoreRounded
                    sx={{
                        transform: expanded ? "rotate(180deg)" : "none",
                        transition: "transform 120ms",
                    }}
                />
                <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 800 }}>
                        Analysis-only results
                    </Typography>
                    <Typography sx={{ color: pageColors.muted, fontSize: 13 }}>
                        {results.length} result{results.length === 1 ? "" : "s"}{" "}
                        not surfaced as a report or explored outcome
                    </Typography>
                </Box>
            </Box>
            <Collapse in={expanded} timeout={180} mountOnEnter>
                <Box
                    sx={{
                        borderTop: "1px dashed",
                        borderColor: "divider",
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        p: 1.5,
                    }}
                >
                    {results.map((item) => {
                        const resultId = item.result.resultId!;
                        const resultExpanded = expandedResultIds.has(resultId);
                        return (
                            <Paper
                                id={`analysis-only-result-${resultId}`}
                                key={resultId}
                                variant="outlined"
                                sx={{
                                    borderRadius: 1.5,
                                    overflow: "hidden",
                                    scrollMarginTop: 16,
                                }}
                            >
                                <Box
                                    component="button"
                                    type="button"
                                    onClick={() =>
                                        onResultExpandedChange(
                                            resultId,
                                            !resultExpanded,
                                        )
                                    }
                                    sx={{
                                        alignItems: "flex-start",
                                        bgcolor: "transparent",
                                        border: 0,
                                        color: pageColors.ink,
                                        cursor: "pointer",
                                        display: "flex",
                                        font: "inherit",
                                        gap: 1,
                                        p: 1.5,
                                        textAlign: "left",
                                        width: "100%",
                                    }}
                                >
                                    <Box sx={{ flex: 1 }}>
                                        <Typography sx={{ fontWeight: 750 }}>
                                            {item.result.name ??
                                                "Analysis result"}
                                            <Box
                                                component="span"
                                                sx={{
                                                    color: pageColors.muted,
                                                    fontSize: 12,
                                                    fontWeight: 600,
                                                    ml: 0.75,
                                                }}
                                            >
                                                Depth {item.node.depth ?? 0}
                                            </Box>
                                        </Typography>
                                        <Typography
                                            sx={{
                                                color: pageColors.muted,
                                                fontSize: 13,
                                                mt: 0.25,
                                            }}
                                        >
                                            {item.pursuedDeeper
                                                ? "Pursued deeper"
                                                : "Not pursued deeper"}
                                        </Typography>
                                    </Box>
                                    {resultMetricLabel(item.result) && (
                                        <Chip
                                            label={resultMetricLabel(
                                                item.result,
                                            )}
                                            size="small"
                                            sx={{
                                                alignSelf: "center",
                                                bgcolor: "grey.100",
                                            }}
                                        />
                                    )}
                                    <ExpandMoreRounded
                                        sx={{
                                            transform: resultExpanded
                                                ? "rotate(180deg)"
                                                : "none",
                                            transition: "transform 120ms",
                                        }}
                                    />
                                </Box>
                                <Collapse
                                    in={resultExpanded}
                                    timeout={180}
                                    mountOnEnter
                                >
                                    <Box
                                        sx={{
                                            borderTop: "1px solid",
                                            borderColor: "divider",
                                            p: 1.5,
                                        }}
                                    >
                                        <DetailText
                                            label="What KBo observed"
                                            value={item.result.claim}
                                        />
                                        <DetailText
                                            label="Supporting analysis"
                                            value={item.result.reasoning}
                                        />
                                        <DetailText
                                            label="Dimension"
                                            value={
                                                item.node.analysis?.dimension
                                                    ?.alias
                                            }
                                        />
                                        <Typography
                                            sx={{
                                                color: pageColors.ink,
                                                fontWeight: 800,
                                                mt: 2,
                                            }}
                                        >
                                            How KBo Got Here
                                        </Typography>
                                        {item.path.length ? (
                                            <HowKboGotHereTimeline
                                                entries={getHowKboGotHereEntriesForPath(
                                                    item.path,
                                                )}
                                                onNavigateToResult={
                                                    onNavigateToResult
                                                }
                                                resultStatus={resultStatus}
                                                runId={runId}
                                            />
                                        ) : (
                                            <Alert
                                                severity="info"
                                                sx={{ mt: 1 }}
                                            >
                                                The retained run does not
                                                include a provenance path for
                                                this result.
                                            </Alert>
                                        )}
                                    </Box>
                                </Collapse>
                            </Paper>
                        );
                    })}
                </Box>
            </Collapse>
        </Paper>
    );
}

function ExploredResultCard({
    result,
    index,
    expanded,
    onExpandedChange,
    onNavigateToResult,
    resultStatus,
    runId,
}: {
    result: V7ExploredLeafResult;
    index: number;
    expanded: boolean;
    onExpandedChange: (expanded: boolean) => void;
    onNavigateToResult: (resultId: string) => void;
    resultStatus: (resultId: string) => ResultOutcome | undefined;
    runId?: string;
}) {
    const pathEntries = getHowKboGotHereEntriesForPath(result.path);
    const measuredEntry = pathEntries.at(-1);
    const metric = measuredEntry ? timelineMetric(measuredEntry) : undefined;
    const share = measuredEntry ? formatPercent(measuredEntry.share) : "—";
    return (
        <Paper
            id={
                result.result.resultId
                    ? `explored-result-${result.result.resultId}`
                    : undefined
            }
            variant="outlined"
            sx={{
                borderColor: "divider",
                borderRadius: 1.5,
                overflow: "hidden",
            }}
        >
            <Box
                component="button"
                type="button"
                onClick={() => onExpandedChange(!expanded)}
                sx={{
                    alignItems: "flex-start",
                    bgcolor: "transparent",
                    border: 0,
                    color: pageColors.ink,
                    cursor: "pointer",
                    display: "flex",
                    font: "inherit",
                    gap: 1,
                    p: 1.5,
                    textAlign: "left",
                    width: "100%",
                }}
            >
                <Box
                    sx={{
                        alignItems: "center",
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: "50%",
                        color: pageColors.blue,
                        display: "flex",
                        flex: "0 0 auto",
                        fontSize: 12,
                        fontWeight: 800,
                        height: 24,
                        justifyContent: "center",
                        width: 24,
                    }}
                >
                    {index}
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 750 }}>
                        {result.result.name ?? "Explored result"}
                    </Typography>
                    <Typography
                        sx={{ color: pageColors.muted, fontSize: 13, mt: 0.25 }}
                    >
                        Not included in report
                    </Typography>
                </Box>
                {(metric || share !== "—") && (
                    <Chip
                        label={`${metric ?? "Metric not retained"}${
                            share !== "—" ? ` · ${share}` : ""
                        }`}
                        size="small"
                        sx={{ alignSelf: "center", bgcolor: "grey.100" }}
                    />
                )}
                <ExpandMoreRounded
                    sx={{
                        transform: expanded ? "rotate(180deg)" : "none",
                        transition: "transform 120ms",
                    }}
                />
            </Box>
            <Collapse in={expanded} timeout={180} mountOnEnter>
                <Box
                    sx={{
                        borderTop: "1px solid",
                        borderColor: "divider",
                        p: 1.5,
                    }}
                >
                    <DetailText
                        label="What KBo observed"
                        value={result.result.claim}
                    />
                    <DetailText
                        label="Supporting analysis"
                        value={result.result.reasoning}
                    />
                    <Box sx={{ mt: 1.25 }}>
                        <Typography
                            sx={{
                                color: pageColors.muted,
                                fontSize: 11,
                                fontWeight: 800,
                                letterSpacing: "0.05em",
                                textTransform: "uppercase",
                            }}
                        >
                            Why KBo stopped here
                        </Typography>
                        {result.stopReason ? (
                            <TimelineRationale text={result.stopReason} />
                        ) : (
                            <Typography
                                sx={{
                                    color: pageColors.muted,
                                    fontSize: 13,
                                    mt: 0.25,
                                }}
                            >
                                No stopping rationale was retained for this
                                result.
                            </Typography>
                        )}
                    </Box>
                    <Typography
                        sx={{ color: pageColors.ink, fontWeight: 800, mt: 2 }}
                    >
                        How KBo Got Here
                    </Typography>
                    {pathEntries.length ? (
                        <HowKboGotHereTimeline
                            entries={pathEntries}
                            onNavigateToResult={onNavigateToResult}
                            resultStatus={resultStatus}
                            runId={runId}
                        />
                    ) : (
                        <Alert severity="info" sx={{ mt: 1 }}>
                            The retained run does not include a provenance path
                            for this result.
                        </Alert>
                    )}
                </Box>
            </Collapse>
        </Paper>
    );
}

function RunContext({
    output,
    report,
}: {
    output: KboFlowRunOutput;
    report: KboReport;
}) {
    const nodes = getExploration(output)?.nodes ?? [];
    const root = getV7RootNode(output, report);
    const scope = report.data?.summary?.scope;
    const failed = nodes.filter((node) => statusTone(node.status) === "error");
    const pending = nodes.filter(
        (node) => statusTone(node.status) === "warning",
    );
    const calls = nodes.reduce(
        (count, node) => count + (node.invocations?.length ?? 0),
        0,
    );
    const depth = Math.max(0, ...nodes.map((node) => node.depth ?? 0));
    return (
        <>
            <Paper
                variant="outlined"
                sx={{ borderRadius: 2, p: { xs: 2, md: 2.5 } }}
            >
                <Typography
                    sx={{
                        color: pageColors.ink,
                        fontSize: 24,
                        fontWeight: 800,
                    }}
                >
                    KBo digs in, narrowing as it goes
                </Typography>
                <Typography sx={{ color: pageColors.muted, mt: 0.5 }}>
                    {report.data?.question ??
                        "This report’s exploration path and the outcomes it surfaced."}
                </Typography>
                <Box
                    sx={{
                        display: "grid",
                        gap: 1,
                        gridTemplateColumns: {
                            xs: "repeat(2, minmax(0, 1fr))",
                            md: "repeat(4, minmax(0, 1fr))",
                        },
                        mt: 2,
                    }}
                >
                    <ContextMetric
                        label="Full population"
                        value={formatNumber(
                            scope?.conversationsAnalyzed ??
                                root?.analysis?.analyzedCohortCount ??
                                0,
                        )}
                    />
                    <ContextMetric
                        label="Date range"
                        value={scope?.window ?? "Not recorded"}
                    />
                    <ContextMetric
                        label="Fan-out depth"
                        value={`${depth} ${depth === 1 ? "level" : "levels"}`}
                    />
                    <ContextMetric
                        label="Model calls"
                        value={formatNumber(calls)}
                    />
                </Box>
            </Paper>
            {failed.length ? (
                <Alert severity="error">
                    {failed.length} analysis{" "}
                    {failed.length === 1 ? "branch failed" : "branches failed"};
                    the email report was generated from the completed branches.
                </Alert>
            ) : null}
            {!failed.length && pending.length ? (
                <Alert severity="warning">
                    {pending.length} deeper{" "}
                    {pending.length === 1 ? "branch is" : "branches are"} still
                    pending. The report reflects the completed analysis
                    available when it was generated.
                </Alert>
            ) : null}
        </>
    );
}

function ContextMetric({ label, value }: { label: string; value: string }) {
    return (
        <Box
            sx={{
                borderRight: { md: "1px solid" },
                borderColor: "divider",
                pr: 1,
            }}
        >
            <Typography sx={{ fontSize: 22, fontWeight: 800 }}>
                {value}
            </Typography>
            <Typography sx={{ color: pageColors.muted, fontSize: 13 }}>
                {label}
            </Typography>
        </Box>
    );
}

export function FindingsView({
    output,
    runId,
    report: selectedReport,
    emailSuggestions,
    emailSuggestionsLoading,
    emailSubmitting,
    onSendReportEmail,
    onOpenStepDebug,
}: {
    output: KboFlowRunOutput;
    /** Persisted KboFlowRuns row id, distinct from the workflow output's runId. */
    runId?: string;
    /** When rendered from a question tab, show only that question's report. */
    report?: KboReport;
    emailSuggestions: EmailSuggestion[];
    emailSuggestionsLoading: boolean;
    emailSubmitting: boolean;
    onSendReportEmail: (
        recipients: string[],
        reportIndex: number,
    ) => Promise<boolean>;
    onOpenStepDebug?: (target: FindingsStepDebugTarget) => void;
}) {
    const delivered: DeliveredFinding[] = getV7ReportFindings(output)
        .filter((item) => !selectedReport || item.report === selectedReport)
        .map((item, index) => ({
            ...item,
            id: `${item.reportIndex}-${item.finding.findingId ?? index}`,
        }));
    const reports = selectedReport
        ? [selectedReport]
        : [...new Set(delivered.map((item) => item.report))];
    const primaryReport = selectedReport ?? reports[0];
    const [expandedFindingIds, setExpandedFindingIds] = useState<Set<string>>(
        new Set(),
    );
    const [otherPathsExpanded, setOtherPathsExpanded] = useState(false);
    const [analysisOnlyExpanded, setAnalysisOnlyExpanded] = useState(false);
    const [expandedResultIds, setExpandedResultIds] = useState<Set<string>>(
        new Set(),
    );
    if (!primaryReport) return null;
    const exploredResults = getV7ExploredLeafResults(output, primaryReport);
    const analysisOnlyResults = getV7AnalysisOnlyResults(output, primaryReport);
    const hasOtherPaths = exploredResults.length > 0;
    const resultStatusById = new Map<string, ResultOutcome>();
    const resultTargetById = new Map<
        string,
        { id: string; section: "finding" | "explored" | "analysisOnly" }
    >();
    delivered.forEach((item, index) => {
        if (!item.finding.leafAnalysisResultId) return;
        resultStatusById.set(
            item.finding.leafAnalysisResultId,
            "Included in report",
        );
        resultTargetById.set(item.finding.leafAnalysisResultId, {
            id: `finding-${index + 1}`,
            section: "finding",
        });
    });
    exploredResults.forEach((item) => {
        const resultId = item.result.resultId;
        if (!resultId) return;
        resultStatusById.set(resultId, "Not pursued deeper");
        resultTargetById.set(resultId, {
            id: `explored-result-${resultId}`,
            section: "explored",
        });
    });
    analysisOnlyResults.forEach((item) => {
        const resultId = item.result.resultId;
        if (!resultId) return;
        resultStatusById.set(
            resultId,
            item.pursuedDeeper ? "Pursued deeper" : "Not pursued deeper",
        );
        resultTargetById.set(resultId, {
            id: `analysis-only-result-${resultId}`,
            section: "analysisOnly",
        });
    });
    const setResultExpanded = (resultId: string, expanded: boolean) => {
        setExpandedResultIds((current) => {
            const next = new Set(current);
            if (expanded) next.add(resultId);
            else next.delete(resultId);
            return next;
        });
    };
    const setFindingExpanded = (findingId: string, expanded: boolean) => {
        setExpandedFindingIds((current) => {
            const next = new Set(current);
            if (expanded) next.add(findingId);
            else next.delete(findingId);
            return next;
        });
    };
    const navigateToResult = (resultId: string) => {
        const target = resultTargetById.get(resultId);
        if (!target) return;
        if (target.section === "finding") {
            setFindingExpanded(target.id, true);
        } else {
            setResultExpanded(resultId, true);
            if (target.section === "explored") setOtherPathsExpanded(true);
            else setAnalysisOnlyExpanded(true);
        }
        window.setTimeout(() => {
            document.getElementById(target.id)?.scrollIntoView?.({
                behavior: "smooth",
                block: "start",
            });
        }, 200);
    };
    return (
        <Box
            sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: { xs: "1fr", lg: "250px minmax(0, 1fr)" },
            }}
        >
            <Paper
                component="nav"
                variant="outlined"
                sx={{
                    alignSelf: "start",
                    borderRadius: 2,
                    p: 1.25,
                    position: { lg: "sticky" },
                    top: { lg: 16 },
                }}
            >
                <Typography sx={{ fontWeight: 800, px: 0.75, py: 0.5 }}>
                    On this page
                </Typography>
                <NavLink href="#email-report" label="Email report" />
                <Typography
                    sx={{
                        color: pageColors.muted,
                        fontSize: 11,
                        fontWeight: 800,
                        letterSpacing: "0.06em",
                        mt: 1.25,
                        px: 0.75,
                        textTransform: "uppercase",
                    }}
                >
                    Results in the email
                </Typography>
                {delivered.map((item, index) => (
                    <NavLink
                        key={item.id}
                        href={`#finding-${index + 1}`}
                        label={`${index + 1}. ${
                            item.finding.title ?? "Untitled finding"
                        }`}
                    />
                ))}
                {hasOtherPaths && (
                    <NavLink href="#other-paths" label="Other paths explored" />
                )}
                {analysisOnlyResults.length > 0 && (
                    <NavLink
                        href="#analysis-only-results"
                        label="Analysis-only results"
                    />
                )}
            </Paper>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    minWidth: 0,
                }}
            >
                <RunContext output={output} report={primaryReport} />
                <Paper
                    id="email-report"
                    variant="outlined"
                    sx={{
                        borderRadius: 2,
                        p: { xs: 2, md: 2.5 },
                        scrollMarginTop: 16,
                    }}
                >
                    <Box
                        sx={{
                            alignItems: "center",
                            display: "flex",
                            gap: 1,
                        }}
                    >
                        <Typography
                            component="h2"
                            sx={{ fontSize: 24, fontWeight: 800 }}
                        >
                            What KBo sent
                        </Typography>
                        <Box sx={{ flex: 1 }} />
                        {onOpenStepDebug && (
                            <Tooltip title="Debug report creation">
                                <IconButton
                                    aria-label="Debug report creation"
                                    color="primary"
                                    size="small"
                                    sx={{ p: 0.25 }}
                                    onClick={() =>
                                        onOpenStepDebug({
                                            stepId: "report-assembly",
                                        })
                                    }
                                >
                                    <BugReportRounded sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                    <Typography sx={{ color: pageColors.muted, mt: 0.5 }}>
                        Start with the recipient’s report, then open a result to
                        trace the branch that produced it.
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1.25,
                            mt: 1.75,
                        }}
                    >
                        {reports.map((report) => (
                            <EmailPanel
                                key={`${report.questionId ?? "report"}-${
                                    report.rootExplorationNodeId ?? "root"
                                }`}
                                report={report}
                                reportIndex={
                                    output.reports?.indexOf(report) ?? 0
                                }
                                suggestions={emailSuggestions}
                                suggestionsLoading={emailSuggestionsLoading}
                                submitting={emailSubmitting}
                                onSend={onSendReportEmail}
                            />
                        ))}
                    </Box>
                </Paper>
                <Box>
                    <Typography
                        sx={{
                            color: pageColors.green,
                            fontSize: 12,
                            fontWeight: 800,
                            letterSpacing: "0.06em",
                            textTransform: "uppercase",
                        }}
                    >
                        Delivered in the email
                    </Typography>
                    <Typography sx={{ color: pageColors.muted, mt: 0.25 }}>
                        {delivered.length} findings surfaced for the recipient
                    </Typography>
                </Box>
                {delivered.map((item, index) => (
                    <FindingCard
                        key={item.id}
                        item={item}
                        output={output}
                        number={index + 1}
                        expanded={expandedFindingIds.has(
                            `finding-${index + 1}`,
                        )}
                        onExpandedChange={(expanded) =>
                            setFindingExpanded(`finding-${index + 1}`, expanded)
                        }
                        onNavigateToResult={navigateToResult}
                        resultStatus={(resultId) =>
                            resultStatusById.get(resultId)
                        }
                        runId={runId}
                        onOpenStepDebug={onOpenStepDebug}
                    />
                ))}
                <OtherPaths
                    output={output}
                    report={primaryReport}
                    expanded={otherPathsExpanded}
                    onExpandedChange={setOtherPathsExpanded}
                    expandedResultIds={expandedResultIds}
                    onResultExpandedChange={setResultExpanded}
                    onNavigateToResult={navigateToResult}
                    resultStatus={(resultId) => resultStatusById.get(resultId)}
                    runId={runId}
                />
                <AnalysisOnlyResults
                    results={analysisOnlyResults}
                    expanded={analysisOnlyExpanded}
                    onExpandedChange={setAnalysisOnlyExpanded}
                    expandedResultIds={expandedResultIds}
                    onResultExpandedChange={setResultExpanded}
                    onNavigateToResult={navigateToResult}
                    resultStatus={(resultId) => resultStatusById.get(resultId)}
                    runId={runId}
                />
            </Box>
        </Box>
    );
}

function NavLink({ href, label }: { href: string; label: string }) {
    return (
        <Box
            component="a"
            href={href}
            sx={{
                color: pageColors.ink,
                display: "block",
                fontSize: 13,
                fontWeight: 700,
                overflow: "hidden",
                px: 0.75,
                py: 0.75,
                textDecoration: "none",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                "&:hover": { color: pageColors.blue },
            }}
        >
            {label}
        </Box>
    );
}
